import { useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagService } from '@atlassian/jira-flags';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { BoardIssue, BoardIssueOld } from '../../common/types';
import type { JIS2_JiraBusinessBoardSubtaskItems_subtaskIssues_jwmViewItems_JiraWorkManagementViewItemConnection_edges as JIS2ItemEdge } from './__generated_apollo__/JIS2_JiraBusinessBoardSubtaskItems';
import { SUBTASK_FIELD_IDS, QUERY_NAME } from './constants';
import { JIS2_BOARD_PARENT_ISSUE_QUERY } from './gql';
import messages from './messages';
import { transformSubtaskOld } from './utils';

/** @deprecated use useFetchBoardIssues. Remove when jwm.board-awesome-enhancements is cleaned up */
export const useGetSubtasks = () => {
	const cloudId = useCloudId();
	const client = useApolloClient();
	const { showFlag } = useFlagService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/** @deprecated */
	const getSubtasks = useCallback(
		async ({
			parentIssueIds = [],
			issueIds = [],
		}: {
			parentIssueIds?: number[];
			issueIds?: number[];
		}): Promise<{
			data?: BoardIssueOld[];
			subtasks?: BoardIssue[];
			error?: Error;
		}> => {
			try {
				let jql = '';
				if (parentIssueIds.length > 0) {
					jql += `parent in ('${parentIssueIds.join("', '")}')`;
					if (issueIds.length > 0) {
						jql = `(${jql} or id in ('${issueIds.join("', '")}'))`;
					}
				} else if (issueIds.length > 0) {
					jql += `id in ('${issueIds.join("', '")}')`;
				} else {
					return { data: [] };
				}
				jql += ' and hierarchyLevel = -1';

				const response = await client.query({
					// no-cache is important as we cannot use a cached response when a JRT update comes through
					// and we reload the subtasks, or the data will be stale
					fetchPolicy: 'no-cache',
					variables: {
						cloudId,
						jql,
						fieldIds: SUBTASK_FIELD_IDS,
						first: 100,
					},
					query: JIS2_BOARD_PARENT_ISSUE_QUERY,
				});

				fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`);

				const transformedDataOld = response?.data?.subtaskIssues?.jwmViewItems?.edges
					?.map((edge: JIS2ItemEdge) => transformSubtaskOld(edge?.node))
					.filter((subtask: BoardIssueOld) => !!subtask);

				return { data: transformedDataOld, subtasks: [] };
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				showFlag({
					type: 'error',
					title: messages.getSubtasksErrorTitle,
					description: messages.getSubtasksErrorMessage,
				});

				fireErrorAnalytics({
					meta: {
						id: QUERY_NAME,
						packageName: 'jiraWorkManagementBoard',
						teamName: 'wanjel',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				return { error };
			}
		},
		[client, cloudId, createAnalyticsEvent, showFlag],
	);

	return {
		getSubtasks,
	};
};
